import moment from 'moment';
import { useEffect, useState } from 'react';
import { Row, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import JobsTable from 'views/jobs/components/JobsTable';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import { getJobsWithInvoices } from 'store/jobs/client';
import { JobResponse } from 'store/jobs/job';
import Button from 'components/new-ui/base/button/Button';
import { useJobsFilters } from 'views/jobs/useJobsFiters';
import AppliedJobFilters from 'views/jobs/components/AppliedJobFilters';
import JobFiltersModal from 'views/jobs/components/JobFiltersModal';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import useIndividuals from 'hooks/useIndividuals';
import useCustomers from 'hooks/useCustomers';
import useTeams from 'hooks/useTeams';

const AccountManagerDashboard = () => {
    const navigate = useNavigate();
    const { listIndividuals } = useIndividuals();
    const { listCustomers } = useCustomers();
    const { listTeams } = useTeams();

    const {
        data,
        isLoading,
        queryInputVal,
        exactDate,
        tags,
        archived,
        clearFilter,
        clearAll,
        pagination,
        sortDirection,
        sort,
        filters,
        setArchived,
        handleFiltersChange,
        handleTableChange,
        handleSearch,
        handleClearSearch,
        handleExactDateChange,
    } = useJobsFilters((p) => p, getJobsWithInvoices);

    const [openFiltersModal, setOpenFiltersModal] = useState(false);

    // Refresh the resources in memory
    useEffect(() => {
        listIndividuals({ size: 200 });
        listCustomers({ size: 200 });
        listTeams({ size: 200 });
    }, [listIndividuals, listCustomers, listTeams]);

    return (
        <>
            <StyledPageWrapper>
                <Space style={{ width: '100%' }} direction="vertical" size={24}>
                    <PageHeader title="Dashboard" icon={<HomeOutlined />} />

                    <Row justify="space-between">
                        <Space size={8}>
                            <SearchBar
                                inputPlaceholder="Search Jobs"
                                value={queryInputVal}
                                onSearch={handleSearch}
                                onClear={handleClearSearch}
                            />
                            <Button onClick={() => setOpenFiltersModal(true)}>Filter</Button>
                        </Space>
                        <Space size={8}>
                            <ArchiveFilter
                                value={archived ? 'archived' : 'active'}
                                onChange={(val) => {
                                    if (val === 'archived') {
                                        setArchived(true);
                                    } else {
                                        setArchived(undefined);
                                    }
                                }}
                            />
                            <DatePicker
                                value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                                onChange={handleExactDateChange}
                            />
                        </Space>
                    </Row>

                    <Row>
                        <AppliedJobFilters tags={tags} clearFilter={clearFilter} clearAll={clearAll} />
                    </Row>

                    <JobsTable
                        loading={isLoading}
                        jobs={data?.list ?? []}
                        onRowClick={(record: JobResponse) => {
                            const isUnderManifest = Boolean(record.manifest?.id);
                            const to = isUnderManifest
                                ? `/manifests/${record.manifest?.id}/${record.id}`
                                : `/jobs/${record.id}`;
                            navigate(to);
                        }}
                        onChange={handleTableChange}
                        pagination={pagination}
                        currentSortDirection={sortDirection}
                        currentSortParam={sort}
                        optionalColumns={{
                            manifestId: true,
                            assignedTo: true,
                        }}
                    />
                </Space>
            </StyledPageWrapper>

            <JobFiltersModal
                open={openFiltersModal}
                filters={filters}
                onClose={() => setOpenFiltersModal(false)}
                onClearAll={clearAll}
                onClearFilter={clearFilter}
                onFiltersChange={handleFiltersChange}
                optionalFilters={{
                    assignedTo: true,
                    team: true,
                    customer: true,
                }}
            />
        </>
    );
};

export default AccountManagerDashboard;
